// Turning notifications on, for whoever is signed in on this device.
//
// Two independent switches, because they fail in different ways and a user needs to understand
// which one is off:
//   • Alerts in the app — a chime and a toast while the app is open. Always available.
//   • Notifications on this device — Web Push, which survives the app being closed, needs the
//     user's permission, and on iPhone needs the app installed to the Home Screen first.
import { useCallback, useEffect, useState } from 'react';
import { alertsMuted, ring, setAlertsMuted, unlockAudio } from '../lib/chime.ts';
import { disablePush, enablePush, isStandalone, pushState, sendTestPush, type PushState } from '../lib/push.ts';
import { IconAlert, IconCheck } from './icons.tsx';

export function NotificationSettings({ className = '' }: { className?: string }) {
  const [state, setState] = useState<PushState | null>(null);
  const [busy, setBusy] = useState(false);
  const [muted, setMuted] = useState(alertsMuted());
  const [note, setNote] = useState<string | null>(null);

  const refresh = useCallback(() => {
    pushState()
      .then(setState)
      .catch(() => setState('unsupported'));
  }, []);
  useEffect(refresh, [refresh]);

  const toggle = async () => {
    setBusy(true);
    setNote(null);
    try {
      setState(state === 'on' ? await disablePush() : await enablePush());
    } catch (e) {
      setNote(e instanceof Error ? e.message : 'Could not change the setting.');
      refresh();
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true);
    setNote(null);
    try {
      const r = await sendTestPush();
      setNote(r.sent > 0 ? 'Sent - it should appear in a moment.' : 'No device is registered yet.');
    } catch {
      setNote('Could not send the test.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className={`rounded-lg border border-line bg-surface p-4 shadow-level-1 lg:p-5 ${className}`}>
      <h2 className="text-sm font-semibold text-ink">Notifications</h2>
      <p className="mt-0.5 text-xs text-muted">
        We&rsquo;ll tell you when the lab replies, when a case needs your approval, and when it ships.
      </p>

      {/* In-app alerts */}
      <label className="mt-4 flex items-start gap-3">
        <input
          type="checkbox"
          className="mt-1 h-4 w-4 accent-[var(--c-accent)]"
          checked={!muted}
          onChange={(e) => {
            const on = e.target.checked;
            setAlertsMuted(!on);
            setMuted(!on);
            if (on) {
              unlockAudio();
              ring('message'); // let them hear exactly what they just switched on
            }
          }}
        />
        <span className="min-w-0">
          <span className="block text-sm font-medium text-ink">Alerts while the app is open</span>
          <span className="block text-xs text-muted">A chime, a short vibration and an on-screen banner.</span>
        </span>
      </label>

      {/* Web push */}
      <div className="mt-4 border-t border-line pt-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-medium text-ink">Notifications on this device</div>
            <div className="text-xs text-muted">Reaches you when the app is closed.</div>
          </div>
          {(state === 'on' || state === 'off') && (
            <button
              type="button"
              onClick={() => void toggle()}
              disabled={busy}
              className={`tap shrink-0 rounded-md px-4 py-2 text-sm font-semibold disabled:opacity-50 ${
                state === 'on' ? 'border border-line text-ink' : 'bg-accent text-accent-fg'
              }`}
            >
              {busy ? 'Working…' : state === 'on' ? 'Turn off' : 'Turn on'}
            </button>
          )}
        </div>

        {state === 'on' && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-status-progress-bg px-2.5 py-1 text-xs font-semibold text-status-progress-fg">
              <IconCheck size={14} />
              On for this device
            </span>
            <button
              type="button"
              onClick={() => void test()}
              disabled={busy}
              className="tap rounded-md border border-line px-3 py-1.5 text-xs font-medium text-ink disabled:opacity-50"
            >
              Send a test
            </button>
          </div>
        )}

        {state === 'needs-install' && (
          <Hint>
            On iPhone and iPad, add Alignova to your Home Screen first (Share → Add to Home Screen),
            then open it from there - Apple only allows notifications for installed apps.
          </Hint>
        )}
        {state === 'denied' && (
          <Hint>
            Notifications are blocked for this site. Turn them back on in your browser&rsquo;s site
            settings, then reload this page.
          </Hint>
        )}
        {state === 'disabled-on-server' && <Hint>Push notifications aren&rsquo;t configured on this server yet.</Hint>}
        {state === 'unsupported' && (
          <Hint>
            This browser can&rsquo;t deliver notifications when the app is closed. In-app alerts still
            work.
            {!isStandalone() && ' Installing the app may help.'}
          </Hint>
        )}
        {note && <p className="mt-2 text-xs text-muted">{note}</p>}
      </div>
    </section>
  );
}

function Hint({ children }: { children: React.ReactNode }) {
  return (
    <p className="mt-3 flex items-start gap-2 rounded-md bg-surface-2 px-3 py-2 text-xs text-muted">
      <span className="mt-0.5 shrink-0">
        <IconAlert size={14} />
      </span>
      <span>{children}</span>
    </p>
  );
}
